import type { AnalysisData, AnalysisCategory, AnalysisFolder, AnalysisChat, ChatMessage } from '@/types/analysis';

const STORAGE_KEY = 'stockerai_analysis';

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function seedData(): AnalysisData {
  const catProveedores: AnalysisCategory = { id: 'cat-proveedores', name: 'Proveedores' };
  const catProductos: AnalysisCategory = { id: 'cat-productos', name: 'Productos' };
  const f1: AnalysisFolder = { id: 'f1', categoryId: catProveedores.id, name: 'Análisis margen Q1' };
  const f2: AnalysisFolder = { id: 'f2', categoryId: catProveedores.id, name: 'Análisis portafolio' };
  const f3: AnalysisFolder = { id: 'f3', categoryId: catProductos.id, name: 'Productos por familia' };
  const f4: AnalysisFolder = { id: 'f4', categoryId: catProductos.id, name: 'Productos por marca' };
  const f0: AnalysisFolder = { id: 'f0-sin-asignar', categoryId: catProveedores.id, name: 'Sin asignar' };
  const chats: AnalysisChat[] = [
    {
      id: 'ch1',
      folderId: f1.id,
      title: 'Margen proveedor A',
      preview: '¿Cuál es el margen actual con el proveedor A en aceites?',
      date: '2025-02-07',
      messages: [
        { role: 'user', content: '¿Cuál es el margen actual con el proveedor A en aceites?' },
        { role: 'assistant', content: 'El margen actual con el proveedor A en la línea de aceites es del 18,5%.' },
      ],
    },
    {
      id: 'ch2',
      folderId: f1.id,
      title: 'Condiciones de pago',
      preview: 'Qué condiciones de pago tenemos con los principales proveedores?',
      date: '2025-02-06',
      messages: [
        { role: 'user', content: 'Qué condiciones de pago tenemos con los principales proveedores?' },
        { role: 'assistant', content: 'Según los datos disponibles, los principales proveedores ofrecen 30 días.' },
      ],
    },
    {
      id: 'ch3',
      folderId: f2.id,
      title: 'Portafolio lácteos',
      preview: 'Resumen de ventas en lácteos este mes',
      date: '2025-02-05',
      messages: [
        { role: 'user', content: 'Resumen de ventas en lácteos este mes' },
        { role: 'assistant', content: 'Las ventas en lácteos en febrero muestran un crecimiento del 12% respecto a enero.' },
      ],
    },
    {
      id: 'ch4',
      folderId: f3.id,
      title: 'Rentabilidad arroz',
      preview: '¿Me conviene comprar 500 unidades de arroz a 0,80?',
      date: '2025-02-08',
      messages: [
        { role: 'user', content: '¿Me conviene comprar 500 unidades de arroz a 0,80?' },
        { role: 'assistant', content: 'Según la demanda prevista y el margen actual, te conviene si vendes por encima de 1,02 €/ud.' },
      ],
    },
    {
      id: 'ch5',
      folderId: f4.id,
      title: 'Nuevo chat',
      preview: 'Comparativa de marcas en aceite de oliva',
      date: '2025-02-04',
      messages: [
        { role: 'user', content: 'Comparativa de marcas en aceite de oliva' },
        { role: 'assistant', content: 'Las tres marcas principales en aceite de oliva tienen márgenes similares; la marca X tiene mejor rotación.' },
      ],
    },
  ];
  return {
    categories: [catProveedores, catProductos],
    folders: [f0, f1, f2, f3, f4],
    chats,
  };
}

export function loadAnalysis(): AnalysisData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as AnalysisData;
      if (parsed.categories?.length && parsed.folders?.length) {
        return {
          categories: parsed.categories,
          folders: parsed.folders,
          chats: parsed.chats || [],
        };
      }
    }
  } catch {
    // ignore
  }
  return seedData();
}

export function saveAnalysis(data: AnalysisData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

export function createChatInFolder(
  data: AnalysisData,
  folderId: string,
  messages: ChatMessage[],
  title?: string,
  supplyContext?: AnalysisChat['supplyContext']
): { data: AnalysisData; chat: AnalysisChat } {
  const firstUser = messages.find((m) => m.role === 'user');
  const titleRes = title || (firstUser ? firstUser.content.slice(0, 50) : 'Nuevo chat');
  const previewRes = firstUser ? firstUser.content.slice(0, 100) : '';
  const chat: AnalysisChat = {
    id: generateId(),
    folderId,
    title: titleRes.length > 50 ? titleRes.slice(0, 47) + '...' : titleRes,
    preview: previewRes,
    date: new Date().toISOString().slice(0, 10),
    messages: [...messages],
    ...(supplyContext && Array.isArray(supplyContext.supplyTableData) && supplyContext.supplyTableData.length > 0
      ? { supplyContext: { supplyTableData: supplyContext.supplyTableData, supplierName: supplyContext.supplierName } }
      : {}),
  };
  return {
    data: { ...data, chats: [...data.chats, chat] },
    chat,
  };
}

export function updateChatMessages(data: AnalysisData, chatId: string, messages: ChatMessage[]): AnalysisData {
  const chat = data.chats.find((c) => c.id === chatId);
  if (!chat) return data;
  const firstUser = messages.find((m) => m.role === 'user');
  const updated: AnalysisChat = {
    ...chat,
    messages: [...messages],
    preview: firstUser ? firstUser.content.slice(0, 100) : chat.preview,
  };
  return {
    ...data,
    chats: data.chats.map((c) => (c.id === chatId ? updated : c)),
  };
}

export function updateChatTitle(data: AnalysisData, chatId: string, title: string): AnalysisData {
  const chat = data.chats.find((c) => c.id === chatId);
  if (!chat) return data;
  const trimmed = title.trim().slice(0, 80) || chat.title;
  return {
    ...data,
    chats: data.chats.map((c) => (c.id === chatId ? { ...c, title: trimmed } : c)),
  };
}

export function moveChatToFolder(data: AnalysisData, chatId: string, folderId: string): AnalysisData {
  return {
    ...data,
    chats: data.chats.map((c) => (c.id === chatId ? { ...c, folderId } : c)),
  };
}

export function createFolder(data: AnalysisData, categoryId: string, name: string): { data: AnalysisData; folder: AnalysisFolder } {
  const folder: AnalysisFolder = { id: generateId(), categoryId, name };
  return {
    data: { ...data, folders: [...data.folders, folder] },
    folder,
  };
}

export function createCategory(data: AnalysisData, name: string): { data: AnalysisData; category: AnalysisCategory } {
  const category: AnalysisCategory = { id: generateId(), name };
  return {
    data: { ...data, categories: [...data.categories, category] },
    category,
  };
}

export function deleteChat(data: AnalysisData, chatId: string): AnalysisData {
  return {
    ...data,
    chats: data.chats.filter((c) => c.id !== chatId),
  };
}

/** Folder id for "Sin asignar" (seed name). Used when deleting a folder to move chats. */
function getSinAsignarFolderId(data: AnalysisData): string | undefined {
  return data.folders.find((f) => f.name === 'Sin asignar')?.id;
}

export function deleteFolder(data: AnalysisData, folderId: string): AnalysisData {
  const sinAsignarId = getSinAsignarFolderId(data);
  const nextChats = data.chats.map((c) =>
    c.folderId === folderId && sinAsignarId ? { ...c, folderId: sinAsignarId } : c
  );
  return {
    ...data,
    folders: data.folders.filter((f) => f.id !== folderId),
    chats: nextChats,
  };
}

export function deleteAllChats(data: AnalysisData): AnalysisData {
  return { ...data, chats: [] };
}

/** Elimina los chats sin etiqueta: carpeta "Sin asignar" o carpeta inexistente (no en Proveedores/Productos). */
export function deleteChatsWithoutLabel(data: AnalysisData): AnalysisData {
  const keep = data.chats.filter((c) => {
    const folder = data.folders.find((f) => f.id === c.folderId);
    const hasLabel = folder && folder.name !== 'Sin asignar';
    return hasLabel;
  });
  return { ...data, chats: keep };
}
